import { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { geoCentroid } from "d3-geo";

const GEO_URL = "/india-states.geojson";

function geoStyle(isActive, isSelected) {
  return {
    default: {
      fill: isActive ? (isSelected ? "#0C3244" : "#76E39A") : "rgba(12,50,68,0.06)",
      stroke: "#0C3244",
      strokeWidth: 0.4,
      strokeOpacity: 0.35,
      outline: "none",
      cursor: isActive ? "pointer" : "default",
      transition: "fill 300ms ease",
    },
    hover: {
      fill: isActive ? "#0C3244" : "rgba(12,50,68,0.12)",
      stroke: "#0C3244",
      strokeWidth: 0.5,
      outline: "none",
    },
    pressed: { fill: "#0C3244", outline: "none" },
  };
}

function MapTooltip({ name, count }) {
  return (
    <div className="pointer-events-none absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-ptf-primary text-ptf-bg px-4 py-2 text-center" data-testid="map-tooltip">
      <p className="font-display uppercase text-lg leading-none tracking-tight">{name}</p>
      <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-accent mt-1">
        {count > 0 ? `${count} ${count > 1 ? "entries" : "entry"}` : "No published work yet"}
      </p>
    </div>
  );
}

// counts: object mapping geojson st_nm -> number of programmes
export default function IndiaMap({ counts = {}, selected = "", onSelect }) {
  const [hovered, setHovered] = useState(null);
  const hasActive = Object.keys(counts).length > 0;

  return (
    <div className="relative w-full">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 900, center: [81.5, 22.5] }}
        width={640}
        height={720}
        style={{ width: "100%", height: "auto" }}
        data-testid="india-map"
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) => (
            <>
              {geographies.map((geo) => {
                const name = geo.properties.st_nm;
                const count = counts[name] || 0;
                const isActive = count > 0;
                const isSelected = selected === name;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    data-testid={`state-${name}`}
                    onMouseEnter={() => setHovered({ name, count })}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => isActive && onSelect && onSelect(isSelected ? "" : name)}
                    className={isActive ? "rsm-geo-active" : ""}
                    style={geoStyle(isActive, isSelected)}
                  />
                );
              })}
              {geographies
                .filter((geo) => (counts[geo.properties.st_nm] || 0) > 0)
                .map((geo) => (
                  <Marker key={`pin-${geo.rsmKey}`} coordinates={geoCentroid(geo)} style={{ default: { pointerEvents: "none" }, hover: { pointerEvents: "none" }, pressed: { pointerEvents: "none" } }}>
                    <circle r={10} className="map-pin-halo" style={{ pointerEvents: "none" }} />
                    <circle r={3.2} className="map-pin-core" style={{ pointerEvents: "none" }} />
                  </Marker>
                ))}
            </>
          )}
        </Geographies>
      </ComposableMap>
      <p className="mt-2 text-[0.6rem] uppercase tracking-[0.16em] text-ptf-text/45 md:hidden">Tap a lit state to see its work</p>

      {hovered && <MapTooltip name={hovered.name} count={hovered.count} />}

      {!hasActive && (
        <p className="mt-4 text-center text-sm italic font-serif-ed text-ptf-text/50">
          Regions will light up here as real programmes begin.
        </p>
      )}
    </div>
  );
}
